import { FormattedNumber } from '@sm/intl';
import {
  PackageSkuCosts,
  PackageSkuCost,
  PackageData,
  PackageDataSet,
  PackageSet,
  FeatureSetMap,
  Feature,
} from './summaryPackages';

type PlaceHolderType = {
  label: string;
};
/**
 * Creates a className friendly string from a package label
 *
 * @param {PlaceHolderType} packageObj The package object to create the class name from
 * @return The label name, ready to be used as a className
 */
export function packageLabelToClassName(packageObj: PlaceHolderType): string {
  return packageObj.label.trim().replace(/\s/g, '-').toLowerCase();
}

/**
 *
 * @param skuCosts
 * @returns The package set cost information based on the subscription type
 */

export function getCoreCostFromSkus(skuCosts: PackageSkuCosts): PackageSkuCost | undefined {
  return skuCosts.find(sku => sku?.skuType === 'SUBSCRIPTION_CORE_SEAT');
}

/**
 * Given two skuCosts, returns the rounded percentage savings between them
 *
 * @param {object} skuCostOriginal The originally priced skuCost object
 * @param {object} skuCostDiscounted The discounted priced skuCost object
 */
export function calculateSavingsPercent(skuCostOriginal: PackageSkuCost, skuCostDiscounted: PackageSkuCost): number {
  let retVal = 0;
  const costDifference = skuCostOriginal?.cost - skuCostDiscounted?.cost;

  // If the "discounted" price is greater, we'll just ignore that...
  if (costDifference > 0) {
    retVal = Math.round((costDifference / skuCostOriginal?.cost) * 100);
  }

  return retVal;
}

export function formatCostInLocaleCurrency(skuCost: PackageSkuCost): React.ReactElement | string {
  return FormattedNumber({
    value: skuCost?.cost / 100,
    formatStyle: 'currency',
    currency: skuCost?.currency?.code,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
  });
}

export function monthlyCostInDollars(skuCost: PackageSkuCost): number {
  return skuCost.cost / 100 / 12;
}

export function formatMonthlyCostInLocaleCurrency(skuCost: PackageSkuCost): React.ReactElement | string {
  const monthlyCost = monthlyCostInDollars(skuCost);
  return FormattedNumber({
    value: monthlyCost,
    formatStyle: 'currency',
    currency: skuCost.currency.code,
    currencyDisplay: 'symbol',
    minimumFractionDigits: 0,
  });
}

export function createPackagesList(packages: PackageDataSet, packageSet: PackageSet): PackageDataSet {
  // The provided package list is both the filter and determines the order
  return packages
    .filter((packageObj: PackageData) => packageSet.includes(packageObj.package))
    .sort((a, b) => packageSet.indexOf(a.package) - packageSet.indexOf(b.package));
}

export function convertFeatureSetToMap(featureSet: Feature[]): FeatureSetMap {
  return featureSet.reduce(
    (acc: FeatureSetMap, feature: Feature) => ({
      ...acc,
      [feature.name]: feature,
    }),
    {}
  );
}
